import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// components
import InvoiceListRows from "./InvoiceListRows";
import Loading from "../../components/Loading";
import ErrorDisplay from "../../components/ErrorDisplay";

// styles
import "../project/Project.css";

// global
import {
  accountDataStore,
  companyStore,
  projectStore,
} from "../../store/Store";

// hooks
import useGetFetch from "../../hooks/useGetFetch";
import InvoiceStatusFilter from "../../invoiceComponents/InvoiceStatusFilter";

// data
import {
  invoiceStatusFilterMap,
  invoiceStatus,
} from "../../data/collections-data";
import UpdatePopup from "../../components/UpdatePopup";
import DeletePopup from "../../components/DeletePopup";
import AddAdjustment from "../account-invoices/AddAdjustment";
import SelectFscMethodForDownload from "../../invoiceComponents/SelectFscMethodForDownload";

export default function InvoiceList({ isAsset }) {
  const currentProject = projectStore((data) => data.currentProject);
  const projectCustomerInvoices = projectStore(
    (data) => data.projectCustomerInvoices,
  );
  const setProjectCustomerInvoices = projectStore(
    (data) => data.setProjectCustomerInvoices,
  );
  const setProjectCustomerInvoiceTotals = projectStore(
    (data) => data.setProjectCustomerInvoiceTotals,
  );
  const setProjectSandRates = projectStore((data) => data.setProjectSandRates);
  const setProjectLoads = projectStore((data) => data.setProjectLoads);
  const setProjectAssetLoads = projectStore(
    (data) => data.setProjectAssetLoads,
  );
  const invoiceCounts = companyStore((data) => data.invoiceCounts);
  const setInvoiceCounts = companyStore((data) => data.setInvoiceCounts);
  const setInvoiceAdjustments = accountDataStore(
    (data) => data.setInvoiceAdjustments,
  );

  const [filter, setFilter] = useState("in_progress");
  const [displayInvoices, setDisplayInvoices] = useState([]);
  const [deleteTrigger, setDeleteTrigger] = useState(false);
  const [updateTrigger, setUpdateTrigger] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [body, setBody] = useState();
  const [adjustmentTrigger, setAdjustmentTrigger] = useState(false);
  const [fscMethodTrigger, setFscMethodTrigger] = useState(false);
  const [fscMethod, setFscMethod] = useState(0);
  const { id } = useParams("id");

  // get invoice counts
  const { isPending: countsPending, error: countsError } = useGetFetch(
    `${process.env.REACT_APP_BACKEND}/lc/invoice-counts/${id}`,
    setInvoiceCounts,
  );

  const { isPending: adjustmentsPending, error: adjustmentsError } =
    useGetFetch(
      `${process.env.REACT_APP_BACKEND}/lc/invoice-adjustments/${id}?is_vendor=false&is_received=false`,
      setInvoiceAdjustments,
    );

  const { isPending: sandPending, error: sandError } = useGetFetch(
    `${process.env.REACT_APP_BACKEND}/pc/sand-rates/${currentProject.project.id}`,
    setProjectSandRates,
  );
  // filter invoices based on status
  useEffect(() => {
    var invoices = [];
    if (projectCustomerInvoices && !isAsset) {
      switch (filter) {
        case "in_progress":
          invoices = projectCustomerInvoices.filter(
            (x) =>
              (x.invoice_status_id === 25 || x.invoice_status_id === 21) &&
              !x.is_asset,
          );
          break;
        case "approved":
          invoices = projectCustomerInvoices.filter(
            (x) => x.invoice_status_id === 23 && !x.is_asset,
          );
          break;
        case "paid":
          invoices = projectCustomerInvoices.filter(
            (x) => x.invoice_status_id === 24 && !x.is_asset,
          );
          break;
        case "disputed":
          invoices = projectCustomerInvoices.filter(
            (x) => x.invoice_status_id === 22 && !x.is_asset,
          );
          break;
        default:
          invoices = projectCustomerInvoices.filter(
            (x) =>
              (x.invoice_status_id === 25 || x.invoice_status_id === 21) &&
              !x.is_asset,
          );
      }
    } else if (projectCustomerInvoices && isAsset) {
      switch (filter) {
        case "in_progress":
          invoices = projectCustomerInvoices.filter(
            (x) =>
              (x.invoice_status_id === 25 || x.invoice_status_id === 21) &&
              x.is_asset,
          );
          break;
        case "approved":
          invoices = projectCustomerInvoices.filter(
            (x) => x.invoice_status_id === 23 && x.is_asset,
          );
          break;
        case "paid":
          invoices = projectCustomerInvoices.filter(
            (x) => x.invoice_status_id === 24 && x.is_asset,
          );
          break;
        case "disputed":
          invoices = projectCustomerInvoices.filter(
            (x) => x.invoice_status_id === 22 && x.is_asset,
          );
          break;
        default:
          invoices = projectCustomerInvoices.filter(
            (x) =>
              (x.invoice_status_id === 25 || x.invoice_status_id === 21) &&
              x.is_asset,
          );
      }
    }
    setDisplayInvoices(invoices);
  }, [projectCustomerInvoices, isAsset, filter]);

  if (countsPending || adjustmentsPending || sandPending) {
    return <Loading />;
  }
  if (countsError || adjustmentsError || sandError) {
    return <ErrorDisplay message={"Something Went Wrong"} code={500} />;
  }
  return !isAsset ? (
    <div className="container-fluid">
      <div className="row g-2">
        <div className="load-display-list">
          <InvoiceStatusFilter
            setFilter={setFilter}
            invoiceCounts={invoiceCounts.customer_freight_counts}
          />
          {displayInvoices.length > 0 ? (
            <table className="table table-sm table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Loads</th>
                  <th>Canceled Loads</th>
                  <th>Weight (tons)</th>
                  <th>Calc Weight (tons)</th>
                  <th>Weight Diff (tons)</th>
                  <th>Rev</th>
                  <th>Calc Rev</th>
                  <th>Rev Diff</th>
                  <th>Cost</th>
                  <th>Calc Cost</th>
                  <th>Cost Diff</th>
                  <th>Adjustments</th>
                  <th>Net</th>
                  <th>Profit</th>
                  <th>Margin</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <InvoiceListRows
                  setUpdateTrigger={setUpdateTrigger}
                  isAsset={false}
                  setBody={setBody}
                  invoices={displayInvoices}
                  setDeleteTrigger={setDeleteTrigger}
                  setDeleteId={setDeleteId}
                  setAdjustmentTrigger={setAdjustmentTrigger}
                  setFscMethodTrigger={setFscMethodTrigger}
                  fscMethod={fscMethod}
                  fscMethodTrigger={fscMethodTrigger}
                />
              </tbody>
            </table>
          ) : (
            <p className="no-docs-added">
              No {invoiceStatusFilterMap[filter]} have been added🧐
            </p>
          )}
        </div>
        {updateTrigger && (
          <UpdatePopup
            setTrigger={setUpdateTrigger}
            url={`${process.env.REACT_APP_BACKEND}/pc/customer-invoice`}
            method="PATCH"
            title={`Are you sure you want to update ${body.invoice_name} to ${
              invoiceStatus[body.invoice_status]
            }`}
            errorMessage="Could not update invoice"
            successMessage="Invoice updated"
            setState={setProjectCustomerInvoices}
            body={body}
            invoice={true}
            dataKey="customer_invoices"
          />
        )}
        {deleteTrigger && (
          <DeletePopup
            setTrigger={setDeleteTrigger}
            id={deleteId}
            table="project_customer_invoice_tbl"
            endpoint="customer/invoice"
            nameField={"invoice_name"}
            projectId={parseInt(id)}
            setDocId={setDeleteId}
            setStates={[
              setProjectCustomerInvoices,
              setProjectCustomerInvoiceTotals,
              setInvoiceCounts,
              setProjectLoads,
              setProjectAssetLoads,
            ]}
            setStateFields={[
              "project_customer_invoices",
              "project_customer_invoice_totals",
              "invoice_counts",
              "project_loads",
              "project_asset_loads",
            ]}
          />
        )}
        {adjustmentTrigger && (
          <AddAdjustment
            inv={body}
            setTrigger={setAdjustmentTrigger}
            projectId={currentProject.project.id}
            isVendor={false}
            id={0}
            batchInvoices={null}
          />
        )}
        {fscMethodTrigger && (
        <SelectFscMethodForDownload
          setFscMethodTrigger={setFscMethodTrigger}
          setFscMethod={setFscMethod}
        />
      )}
      </div>
    </div>
  ) : (
    <div className="container-fluid">
      <div className="row g-2">
        <div className="load-display-list">
          <InvoiceStatusFilter
            setFilter={setFilter}
            invoiceCounts={invoiceCounts.customer_asset_counts}
          />
          {displayInvoices.length > 0 ? (
            <table className="table table-sm table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Loads</th>
                  <th>Weight (tons)</th>
                  <th>Calc Weight (tons)</th>
                  <th>Weight Diff (tons)</th>
                  <th>Rev</th>
                  <th>Calc Rev</th>
                  <th>Rev Diff</th>
                  <th>Cost</th>
                  <th>Calc Cost</th>
                  <th>Cost Diff</th>
                  <th>Adjustments</th>
                  <th>Net</th>
                  <th>Profit</th>
                  <th>Margin</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <InvoiceListRows
                  setUpdateTrigger={setUpdateTrigger}
                  isAsset={true}
                  setBody={setBody}
                  invoices={displayInvoices}
                  setDeleteTrigger={setDeleteTrigger}
                  setDeleteId={setDeleteId}
                  setAdjustmentTrigger={setAdjustmentTrigger}
                />
              </tbody>
            </table>
          ) : (
            <p className="no-docs-added">
              No {invoiceStatusFilterMap[filter]} have been added🧐
            </p>
          )}
        </div>
        {updateTrigger && (
          <UpdatePopup
            setTrigger={setUpdateTrigger}
            url={`${process.env.REACT_APP_BACKEND}/pc/customer-invoice`}
            method="PATCH"
            title={`Are you sure you want to update ${body.invoice_name} to ${
              invoiceStatus[body.invoice_status]
            }`}
            errorMessage="Could not update invoice"
            successMessage="Invoice updated"
            setState={setProjectCustomerInvoices}
            body={body}
            invoice={true}
            dataKey="customer_invoices"
          />
        )}
        {deleteTrigger && (
          <DeletePopup
            setTrigger={setDeleteTrigger}
            id={deleteId}
            table="project_customer_invoice_tbl"
            endpoint="customer/invoice"
            nameField={"invoice_name"}
            projectId={parseInt(id)}
            setDocId={setDeleteId}
            setStates={[
              setProjectCustomerInvoices,
              setProjectCustomerInvoiceTotals,
              setInvoiceCounts,
              setProjectLoads,
              setProjectAssetLoads,
            ]}
            setStateFields={[
              "project_customer_invoices",
              "project_customer_invoice_totals",
              "invoice_counts",
              "project_loads",
              "project_asset_loads",
            ]}
          />
        )}
        {adjustmentTrigger && (
          <AddAdjustment
            inv={body}
            setTrigger={setAdjustmentTrigger}
            projectId={currentProject.project.id}
            isVendor={false}
            id={0}
            batchInvoices={null}
          />
        )}
      </div>
    </div>
  );
}
